import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectLanguage } from '../redux/cartSlice';
import useWishlist from '../hooks/useWishlist';
import { getProductUrl } from '../utils/seoManager';
import './WishList.css';

const wishlistTranslations = {
  en: {
    title: 'My Wish List',
    subtitle: 'Items you love, saved for later',
    empty: 'Your wishlist is empty',
    emptyHint: 'Click the heart icon on any product to add it here.',
    browseBtn: 'Browse Products',
    remove: 'Remove',
    viewProduct: 'View Product',
    notLoggedIn: 'Please log in to view your wish list.',
    loginBtn: 'Log In',
    addedOn: 'Added on',
  },
  nl: {
    title: 'Mijn Verlanglijst',
    subtitle: 'Producten die u leuk vindt, bewaard voor later',
    empty: 'Uw verlanglijst is leeg',
    emptyHint: 'Klik op het hartje bij een product om het hier op te slaan.',
    browseBtn: 'Producten Bekijken',
    remove: 'Verwijderen',
    viewProduct: 'Product Bekijken',
    notLoggedIn: 'Log in om uw verlanglijst te bekijken.',
    loginBtn: 'Inloggen',
    addedOn: 'Toegevoegd op',
  },
  de: {
    title: 'Meine Wunschliste',
    subtitle: 'Produkte, die Sie mögen, für später gespeichert',
    empty: 'Ihre Wunschliste ist leer',
    emptyHint: 'Klicken Sie auf das Herzsymbol bei einem Produkt, um es hier zu speichern.',
    browseBtn: 'Produkte Durchsuchen',
    remove: 'Entfernen',
    viewProduct: 'Produkt Ansehen',
    notLoggedIn: 'Bitte melden Sie sich an, um Ihre Wunschliste anzuzeigen.',
    loginBtn: 'Einloggen',
    addedOn: 'Hinzugefügt am',
  },
  fr: {
    title: 'Ma Liste de Souhaits',
    subtitle: 'Des articles que vous aimez, sauvegardés pour plus tard',
    empty: 'Votre liste de souhaits est vide',
    emptyHint: 'Cliquez sur le cœur sur n\'importe quel produit pour l\'ajouter ici.',
    browseBtn: 'Parcourir les Produits',
    remove: 'Supprimer',
    viewProduct: 'Voir le Produit',
    notLoggedIn: 'Veuillez vous connecter pour voir votre liste de souhaits.',
    loginBtn: 'Se Connecter',
    addedOn: 'Ajouté le',
  },
  es: {
    title: 'Mi Lista de Deseos',
    subtitle: 'Artículos que te gustan, guardados para más tarde',
    empty: 'Tu lista de deseos está vacía',
    emptyHint: 'Haz clic en el corazón de cualquier producto para añadirlo aquí.',
    browseBtn: 'Ver Productos',
    remove: 'Eliminar',
    viewProduct: 'Ver Producto',
    notLoggedIn: 'Por favor, inicia sesión para ver tu lista de deseos.',
    loginBtn: 'Iniciar Sesión',
    addedOn: 'Añadido el',
  },
  it: {
    title: 'La Mia Lista dei Desideri',
    subtitle: 'Articoli che ami, salvati per dopo',
    empty: 'La tua lista dei desideri è vuota',
    emptyHint: 'Clicca sul cuore su qualsiasi prodotto per aggiungerlo qui.',
    browseBtn: 'Sfoglia i Prodotti',
    remove: 'Rimuovi',
    viewProduct: 'Vedi Prodotto',
    notLoggedIn: 'Accedi per vedere la tua lista dei desideri.',
    loginBtn: 'Accedi',
    addedOn: 'Aggiunto il',
  },
  pt: {
    title: 'Minha Lista de Desejos',
    subtitle: 'Itens que você ama, salvos para depois',
    empty: 'A sua lista de desejos está vazia',
    emptyHint: 'Clique no coração em qualquer produto para adicioná-lo aqui.',
    browseBtn: 'Ver Produtos',
    remove: 'Remover',
    viewProduct: 'Ver Produto',
    notLoggedIn: 'Por favor, inicie sessão para ver a sua lista de desejos.',
    loginBtn: 'Iniciar Sessão',
    addedOn: 'Adicionado em',
  },
  el: {
    title: 'Λίστα Επιθυμιών μου',
    subtitle: 'Προϊόντα που αγαπάτε, αποθηκευμένα για αργότερα',
    empty: 'Η λίστα επιθυμιών σας είναι κενή',
    emptyHint: 'Κάντε κλικ στην καρδιά σε οποιοδήποτε προϊόν για να το προσθέσετε εδώ.',
    browseBtn: 'Περιήγηση Προϊόντων',
    remove: 'Αφαίρεση',
    viewProduct: 'Δείτε Προϊόν',
    notLoggedIn: 'Παρακαλούμε συνδεθείτε για να δείτε τη λίστα επιθυμιών σας.',
    loginBtn: 'Σύνδεση',
    addedOn: 'Προστέθηκε στις',
  }
};

const WishList = ({ siteSettings = {} }) => {
  const selectedLang = useSelector(selectLanguage) || { code: 'en' };
  const t = wishlistTranslations[selectedLang.code] || wishlistTranslations.en;
  const { wishlistItems, toggleWishlist } = useWishlist();

  const getUser = () => {
    try {
      const session = localStorage.getItem('user_session');
      return session ? JSON.parse(session) : null;
    } catch { return null; }
  };

  const user = getUser();

  useEffect(() => {
    document.title = 'Wish List | Smart Telecom Store';
  }, []);

  const navigate = (path) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const productUrl = (item) => getProductUrl({ _id: item.productId, slug: item.slug }, siteSettings);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString(selectedLang.code || 'en', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  if (!user) {
    return (
      <div className="wl-page">
        <div className="wl-container">
          <div className="wl-state-box">
            <div className="wl-state-icon">🔒</div>
            <h2 className="wl-state-title">{t.notLoggedIn}</h2>
            <button className="wl-login-btn" onClick={() => navigate('/login')}>{t.loginBtn}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wl-page">
      <div className="wl-container">

        {/* Page Header */}
        <div className="wl-header">
          <div className="wl-header-text">
            <div className="wl-breadcrumb">
              <span onClick={() => navigate('/')} className="wl-breadcrumb-link">Home</span>
              <span className="wl-breadcrumb-sep">›</span>
              <span>{t.title}</span>
            </div>
            <h1 className="wl-title">{t.title}</h1>
            <p className="wl-subtitle">{t.subtitle}</p>
          </div>
          <div className="wl-count-badge">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#be1e2d" stroke="#be1e2d" strokeWidth="1.5">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
            <span>{wishlistItems.length}</span>
          </div>
        </div>

        {/* Empty State */}
        {wishlistItems.length === 0 ? (
          <div className="wl-state-box">
            <div className="wl-empty-heart">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </div>
            <h2 className="wl-state-title">{t.empty}</h2>
            <p className="wl-state-hint">{t.emptyHint}</p>
            <button className="wl-browse-btn" onClick={() => navigate('/')}>{t.browseBtn}</button>
          </div>
        ) : (
          <div className="wl-grid">
            {wishlistItems.map((item) => (
              <div key={item.productId} className="wl-card">
                {/* Heart remove button */}
                <button
                  className="wl-card-heart"
                  onClick={() => toggleWishlist({ ...item })}
                  title={t.remove}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="#be1e2d" stroke="#be1e2d" strokeWidth="1.5">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                </button>

                {/* Product Image */}
                <div
                  className="wl-card-img-wrap"
                  onClick={() => navigate(productUrl(item))}
                >
                  <img
                    src={item.image || 'https://via.placeholder.com/200x200?text=Product'}
                    alt={item.name}
                    className="wl-card-img"
                  />
                </div>

                {/* Product Info */}
                <div className="wl-card-info">
                  {item.category && (
                    <span className="wl-card-cat">{item.category}</span>
                  )}
                  <h3
                    className="wl-card-name"
                    onClick={() => navigate(productUrl(item))}
                  >
                    {item.name}
                  </h3>
                  {item.price > 0 && (
                    <span className="wl-card-price">€{Number(item.price).toFixed(2)}</span>
                  )}
                  {item.addedAt && (
                    <span className="wl-card-date">{t.addedOn}: {formatDate(item.addedAt)}</span>
                  )}
                  <div className="wl-card-actions">
                    <button
                      className="wl-view-btn"
                      onClick={() => navigate(productUrl(item))}
                    >
                      {t.viewProduct}
                    </button>
                    <button
                      className="wl-remove-btn"
                      onClick={() => toggleWishlist({ ...item })}
                    >
                      {t.remove}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default WishList;
